import { useState, useEffect, useContext, useMemo } from 'react';
import _ from 'underscore';
import _preferencesService from '@bingads-webui-component-legacy/preference/service/index';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getActionText } from '@bingads-webui-campaign-legacy/adextension-actionlink/model/language-type-text-mapper';
import { useWizardService, getFlowFactors } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { FLOWS } from '../step-constants';

// const BiddingSchemeToBidType = {
//   EnhancedCpc: 'SearchBid',
//   ManualCpc: 'SearchBid',
//   ManualCpm: 'CpmBid',
// };

const buildSchemaData = ({
  data,
  timeDisplayType,
  serverContext,
  cid,
  aid,
  flow,
  loadedStoresData,
  savedProductFilter,
  savedListingGroup,
  i18n,
  permissions,
}) => {
  const locations = _.map(
    _.filter(
      data.campaign && data.campaign.locationTargets && data.campaign.locationTargets.currentLevelSettings.currentLocations,
      loc => loc.SelectionStatus === 'Included'
    ),
    loc => loc.location
  );

  const {
    PerformanceMaxCampaignsAutoGenAssetControl: IsPerformanceMaxCampaignsAutoGenAssetControlEnabled,
    SearchThemes: IsSearchThemesEnabled,
    SupportCPSForPmax: IsSupportCPSForPmaxEnabled,
    AssetGroupUrlRules: isAssetGroupUrlRulesEnabled,
    AssetGroupUrlRulesV2: isAssetGroupUrlRulesV2Enabled,
    NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
    PMaxDisclaimers,
  } = permissions.dynamic;

  const isPMaxDisclaimersEnabled = !serverContext.Permissions.IsDomesticSimplifiedChineseEnabled && PMaxDisclaimers && serverContext.Permissions.IsMSANDisclaimerEnabled;
  const isCampaignShoppingType = !!_.find(_.get(data.campaign, 'CampaignSettings', []), setting => setting['@odata.type'] === '#Model.ShoppingSettings');

  return ({
    campaignName: {
      data: data.campaign.Name,
    },
    assetGroupName: {
      data: data.assetGroup.Name,
    },
    autoGeneratedAssets: {
      data: {
        finalUrlExpansionOptOut: data.campaign.finalUrlExpansionOptOut,
        finalUrlExpansions: data.campaign.finalUrlExpansions,
        autoGeneratedTextOptOut: data.campaign.autoGeneratedTextOptOut,
        autoGeneratedImageOptOut: data.campaign.autoGeneratedImageOptOut,
      },
      isRendered: IsPerformanceMaxCampaignsAutoGenAssetControlEnabled,
    },
    budget: {
      data: data.campaign.Budget,
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
    bidStrategy: {
      data: data.campaign.BiddingScheme,
    },
    bidTarget: {
      data: data.campaign.BiddingScheme,
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
    costPerSale: {
      data: {
        costPerSaleOptOut: data.campaign.costPerSaleOptOut,
      },
      isRendered: IsSupportCPSForPmaxEnabled,
    },
    conversionGoals: {
      conversionGoals: data.campaign.conversionGoals,
    },
    disclaimer: {
      data: data.disclaimer,
      isRendered: isPMaxDisclaimersEnabled && !isCampaignShoppingType,
    },
    locationTargeting: {
      locations,
      targetOption: data.campaign && data.campaign.locationTargets && data.campaign.locationTargets.currentLevelSettings.targetOption,
    },
    languages: {
      data: data.campaign.Languages,
    },
    audienceSignal: {
      data: data.assetGroup.AudienceGroup,
    },
    searchThemes: {
      data: data.assetGroup.AssetGroupSearchThemes,
      isRendered: IsSearchThemesEnabled,
    },
    images: {
      data: data.assetGroup.Images,
      shouldFetchFromMT: true,
      cid,
      aid,
    },
    logos: {
      data: data.assetGroup.Images,
      shouldFetchFromMT: true,
      cid,
      aid,
      label: _TL_('Logos'),
      isLogo: true,
    },
    videos: {
      data: data.assetGroup.videos,
      isRendered: data.assetGroup.videos && data.assetGroup.videos.length > 0,
    },
    headlines: {
      data: data.assetGroup.Headlines,
    },
    descriptions: {
      data: data.assetGroup.Descriptions,
    },
    longHeadlines: {
      data: data.assetGroup.LongHeadlines,
    },
    businessName: {
      data: data.assetGroup.BusinessName,
    },
    callToAction: {
      data: data.assetGroup.CallToAction === 'Automated' ? i18n.getString(_TL_('Automated')) : getActionText({ Language: 'English', ActionType: data.assetGroup.CallToAction }),
    },
    adScheduleCampaign: {
      startDate: data.assetGroup.StartDate,
      endDate: data.assetGroup.EndDate,
      adSchedules: data.campaign && data.campaign.adSchedules && data.campaign.adSchedules.dayTimeRanges,
      useSearcherTimeZone: data.campaign.AdScheduleUseSearcherTimeZone,
      timeDisplayType,
    },
    productsPanel: {
      isRendered: flow === FLOWS.RETAIL_PERF_MAX,
    },
    storeInfo: {
      data: {
        storesData: loadedStoresData,
        ProviderId: data.campaign.ProviderId,
        IsFeedFiltered: data.campaign.IsFeedFiltered,
        SalesCountry: data.campaign.SalesCountry,
        FeedLabel: data.campaign.FeedLabel,
        LocalInventoryAdsEnabled: data.campaign.LocalInventoryAdsEnabled,
      },
    },
    listingGroup: {
      data: {
        productFilter: savedProductFilter,
        listingGroup: savedListingGroup,
      },
    },
    urlOptions: {
      data: {
        TrackingUrlTemplate: data.campaign.TrackingUrlTemplate,
        FinalUrlSuffix: data.campaign.FinalUrlSuffix,
        UrlCustomParameters: data.campaign.UrlCustomParameters,
      },
    },
    finalUrlExpansions: {
      data: {
        finalUrlExpansions: data.campaign.finalUrlExpansions,
      },
      isRendered: !IsPerformanceMaxCampaignsAutoGenAssetControlEnabled,
    },
    urlRules: {
      data: {
        i18n,
        finalUrls: data.assetGroup.FinalUrls,
        path1: data.assetGroup.Path1,
        path2: data.assetGroup.Path2,
        mobileFinalUrls: data.assetGroup.MobileFinalUrls,
        urlRules: data.assetGroup.AssetGroupUrlTargets,
      },
      isAssetGroupUrlRulesV2Enabled,
      isRendered: isAssetGroupUrlRulesEnabled,
    },
    newCustomerAcquisition: {
      data: {
        newCustomerAcquisitionOptOut: data.campaign.newCustomerAcquisitionOptOut,
        newCustomerAcquisition: data.campaign.newCustomerAcquisition,
      },
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
      isRendered: isNewCustomerAcquisitionEnabled,
    },
  });
};

export const useSummarySchemaData = ({ data }) => {
  const { scope } = useContext(ScopeContext) || {};

  const i18n = useContext(I18nContext);

  const { cid, aid } = scope.values;

  const [timeDisplayType, setTimeDisplayType] = useState('12-hour');

  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const { state } = useWizardService();

  const { loadedStoresData, savedProductFilter, savedListingGroup } = state.context.wizardData;

  const { flow } = getFlowFactors(state);

  useEffect(() => {
    const preferencesService = _preferencesService.default || _preferencesService;
    preferencesService.initialize().then(() => {
      const isTwelveHourDisplay = preferencesService.findByName('WebApp.CampaignUI.isTwelveHourDisplay') || {};
      setTimeDisplayType(_.isUndefined(isTwelveHourDisplay.Data) || isTwelveHourDisplay.Data ? '12-hour' : '24-hour');
    });
  }, []);

  return (data && buildSchemaData({
    data,
    timeDisplayType,
    serverContext,
    cid,
    aid,
    flow,
    loadedStoresData,
    savedProductFilter,
    savedListingGroup,
    i18n,
    permissions,
  })) || {};
};

